import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Button,
  Modal,
  Form,
  Select,
  message,
  Spin,
  Alert,
  Space,
  Divider,
  Tag,
} from "antd";
import { debounce } from "lodash";
import axios from "axios";
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";

const { Option } = Select;

const ELIGIBLE_ENDPOINT = "/api/users/eligible-main-account";

const REASON_LABELS = {
  hasOrders: "Account has orders",
  hasCredit: "Account has credit balance",
  hasPendingCod: "Account has pending COD",
  hasIntegrations: "Account has Shopify/WooCommerce connected",
  hasSubaccounts: "Account has subaccounts",
  noMerchant: "Account has no merchant",
};

const ROLE_OPTIONS = [
  { key: "admin", label: "Admin" },
  { key: "operation", label: "Operation" },
  { key: "finance", label: "Finance" },
];

function MainAccountPickerModal({
  title,
  okText,
  submitEndpoint,
  excludeIds = [],
  onCancel,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nextCursor, setNextCursor] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const requestSeq = useRef(0);

  // Callers pass a fresh array each render, so key the callbacks on its serialized content.
  const excludeIdsKey = excludeIds.join(",");

  const selectedOptionRef = useRef(null);

  const fetchEligible = useCallback(
    async ({ search = "", lastSeenId = undefined, append = false }) => {
      const seq = ++requestSeq.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setSearching(true);
      }
      try {
        const excludedIds = excludeIdsKey ? excludeIdsKey.split(",") : [];
        const params = { eligibleMainAccount: true, limit: 20 };
        if (search) params.search = search;
        if (excludedIds.length > 0) params.excludeUserIds = excludedIds;
        if (lastSeenId) params.lastSeenId = lastSeenId;

        const res = await axios.get(ELIGIBLE_ENDPOINT, { params });
        if (seq !== requestSeq.current) return;

        const list = res.data?.data || [];
        const cursor = res.data?.nextCursor;

        setOptions((prev) => {
          const filtered = list.filter((item) => !excludedIds.includes(item.id));
          if (append) {
            const existingIds = new Set(prev.map((p) => p.id));
            const fresh = filtered.filter((item) => !existingIds.has(item.id));
            return [...prev, ...fresh];
          }
          // Keep the selected row in the list so AntD can still render its label after a refresh.
          const selected = selectedOptionRef.current;
          if (selected && !filtered.some((item) => item.id === selected.id)) {
            return [selected, ...filtered];
          }
          return filtered;
        });

        // Old API without nextCursor returns undefined, which stops the scroll for good.
        setNextCursor(cursor);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        const msg = err.response?.data?.message || err.message;
        message.error(msg || "Failed to load eligible main accounts");
      } finally {
        if (seq === requestSeq.current) {
          setSearching(false);
          setLoadingMore(false);
        }
      }
    },
    [excludeIdsKey]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        fetchEligible({ search: value, lastSeenId: undefined, append: false });
      }, 400),
    [fetchEligible]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Bump the sequence on unmount so a late response is treated as stale instead of setting state.
  useEffect(() => () => {
    requestSeq.current++;
  }, []);

  // Drop the cursor on every keystroke so the debounce window cannot append the previous term's next
  // page under the new search.
  const handleSearch = (value) => {
    setSearchTerm(value);
    setNextCursor(undefined);
    debouncedSearch(value);
  };

  useEffect(() => {
    form.resetFields();
    setSearchTerm("");
    setNextCursor(undefined);
    fetchEligible({ search: "", lastSeenId: undefined, append: false });
  }, [form, fetchEligible]);

  const handlePopupScroll = (e) => {
    const el = e.target;
    if (!nextCursor || searching || loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchEligible({ search: searchTerm, lastSeenId: nextCursor, append: true });
    }
  };

  const handleSelectChange = (value) => {
    selectedOptionRef.current = options.find((o) => o.id === value) || null;
  };

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(submitEndpoint, {
        mainAccountId: values.mainAccountId,
        role: values.role,
      });
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      message.error(msg || "Failed to update subaccount");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      visible
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={submitting}
      okText={okText}
      forceRender
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="mainAccountId"
          label="Target Main Account"
          rules={[{ required: true, message: "Please select a main account" }]}
        >
          <Select
            showSearch
            placeholder="Search by email or name"
            filterOption={false}
            onSearch={handleSearch}
            onChange={handleSelectChange}
            onPopupScroll={handlePopupScroll}
            loading={searching}
            defaultActiveFirstOption={false}
            notFoundContent={searching ? <Spin size="small" /> : null}
            dropdownRender={(menu) => (
              <div>
                {menu}
                {loadingMore && (
                  <div style={{ textAlign: "center", padding: "8px 0" }}>
                    <Spin size="small" />
                  </div>
                )}
              </div>
            )}
          >
            {options.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name} ({item.email})
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: "Please select a role" }]}
        >
          <Select placeholder="Select role">
            {ROLE_OPTIONS.map((r) => (
              <Option key={r.key} value={r.key}>
                {r.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
}

function UserSubAccountActions({ userId, onChanged }) {
  const { permissions } = usePermissions();
  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [picker, setPicker] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const contextSeq = useRef(0);

  const hasManagePermission = permissions?.includes(PERMISSIONS.MANAGE_SUBACCOUNT);

  const fetchContext = useCallback(async () => {
    if (!userId || !hasManagePermission) return;
    const seq = ++contextSeq.current;
    setLoadingContext(true);
    try {
      const res = await axios.get(`/api/users/${userId}/subaccount-context`);
      if (seq !== contextSeq.current) return;
      setContext(res.data?.data || null);
    } catch (err) {
      if (seq !== contextSeq.current) return;
      setContext(null);
      const msg = err.response?.data?.message || err.message;
      message.error(msg || "Failed to load subaccount context");
    } finally {
      if (seq === contextSeq.current) setLoadingContext(false);
    }
  }, [userId, hasManagePermission]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  const handleConvert = () => {
    if (convertOpen || converting) return;
    setConvertOpen(true);
    Modal.confirm({
      title: "Convert to Main Account",
      content:
        "This starts a fresh standalone account with zero credit and default package; past orders/statements are not moved.",
      okText: "Convert",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setConverting(true);
        try {
          await axios.post(`/api/users/${userId}/subaccount/convert-to-main`, {});
          message.success("Converted to main account successfully");
          setConvertOpen(false);
          await fetchContext();
          onChanged && onChanged();
        } catch (err) {
          const msg = err.response?.data?.message || err.message;
          message.error(msg || "Failed to convert subaccount");
          throw err;
        } finally {
          setConverting(false);
        }
      },
      onCancel: () => setConvertOpen(false),
    });
  };

  if (!hasManagePermission) return null;

  const isEmptyReasons = context?.emptyCheck?.reasons || [];
  const canLinkDisabledReason =
    context && !context.canLink
      ? context.isSubAccount
        ? "This account is already a subaccount"
        : isEmptyReasons.length > 0
        ? isEmptyReasons.map((r) => REASON_LABELS[r] || r).join(", ")
        : "Not eligible to link"
      : null;

  const handlePickerSuccess = async (text) => {
    message.success(text);
    setPicker(null);
    await fetchContext();
    onChanged && onChanged();
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Divider orientation="left">Subaccount Management</Divider>
      {loadingContext && <Spin />}
      {context && (
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div>
              <strong>Status: </strong>
              {context.isSubAccount ? (
                <Tag color="blue">Subaccount</Tag>
              ) : (
                <Tag color="green">Main Account</Tag>
              )}
            </div>
            {context.isSubAccount && context.mainAccountId && (
              <div>
                <strong>Main Account ID: </strong>
                {context.mainAccountId}
              </div>
            )}
            {context.merchantId && (
              <div>
                <strong>Merchant ID: </strong>
                {context.merchantId}
              </div>
            )}
          </Space>
          {canLinkDisabledReason && (
            <Alert
              type="info"
              message="Cannot link"
              description={canLinkDisabledReason}
              style={{ marginTop: 12 }}
              showIcon
            />
          )}
        </div>
      )}
      <Space wrap>
        <Button
          type="primary"
          onClick={() => setPicker("link")}
          disabled={!context?.canLink}
        >
          Link to Main Account
        </Button>
        <Button
          type="default"
          onClick={() => setPicker("move")}
          disabled={!context?.canMove}
        >
          Move Subaccount
        </Button>
        <Button
          type="danger"
          onClick={handleConvert}
          loading={converting}
          disabled={!context?.canConvert}
        >
          Convert to Main
        </Button>
      </Space>

      {picker === "link" && (
        <MainAccountPickerModal
          title="Link to Main Account"
          okText="Link"
          submitEndpoint={`/api/users/${userId}/subaccount/link`}
          excludeIds={[userId]}
          onCancel={() => setPicker(null)}
          onSuccess={() => handlePickerSuccess("Subaccount linked successfully")}
        />
      )}

      {picker === "move" && (
        <MainAccountPickerModal
          title="Move Subaccount"
          okText="Move"
          submitEndpoint={`/api/users/${userId}/subaccount/move`}
          excludeIds={[userId, context?.mainAccountId].filter(Boolean)}
          onCancel={() => setPicker(null)}
          onSuccess={() => handlePickerSuccess("Subaccount moved successfully")}
        />
      )}
    </div>
  );
}

export default UserSubAccountActions;
